// GAVION booking: the public page #/demo/gerer/:token (spec §4): the booking in the viewer's zone, « Choisir un autre
// moment » (the same picker, current slot excluded) and « Annuler la démo » (asked twice). An unusable token shows
// « Lien expiré : contactez <vendeur> ». The token is read from the hash route and only ever sent in a POST body.
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { BOOKING_COPY, fill, langOf, type PublicLang } from "./copy";
import { PublicShell } from "./PublicShell";
import { SlotPicker } from "./SlotPicker";
import { browserZone, whenWithZone } from "./slotGroups";
import { type ManageRep, type ManageView, type PublicBookingApi, PublicBookingError } from "./api/types";

type State =
  | { kind: "loading" }
  | { kind: "ready"; view: ManageView }
  | { kind: "expired"; rep: ManageRep | null }
  | { kind: "error"; code: string };

export const ManageBookingPage = ({ api }: { api: PublicBookingApi }) => {
  const { token = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [mode, setMode] = useState<"idle" | "picking" | "confirmCancel">("idle");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [taken, setTaken] = useState<string[]>([]);
  const [pickerKey, setPickerKey] = useState(0);
  const [moved, setMoved] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // Final review M6 (a11y): the buttons the visitor used disappear on each step (« Choisir un autre moment », « Annuler la
  // démo », « Garder la démo », a move or a cancel done), so focus goes to the line that states the booking.
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const [focusSummary, setFocusSummary] = useState(0);
  useEffect(() => {
    if (focusSummary > 0) summaryRef.current?.focus();
  }, [focusSummary]);
  const step = (m: "idle" | "picking" | "confirmCancel") => {
    setMode(m);
    setFocusSummary((n) => n + 1);
  };
  const asked = params.get("lang");
  const lang: PublicLang = asked ? langOf(asked) : state.kind === "ready" ? state.view.language : "fr";
  const c = BOOKING_COPY[lang];
  const tz = browserZone();

  useEffect(() => {
    let alive = true;
    api.manage(token).then(
      (view) => alive && setState({ kind: "ready", view }),
      (e: unknown) => {
        if (!alive) return;
        if (e instanceof PublicBookingError && e.code === "link_expired") setState({ kind: "expired", rep: e.rep });
        else setState({ kind: "error", code: e instanceof PublicBookingError ? e.code : "error" });
      },
    );
    return () => {
      alive = false;
    };
  }, [api, token, attempt]);

  const setLang = (l: PublicLang) => {
    const p = new URLSearchParams(params);
    p.set("lang", l);
    setParams(p, { replace: true });
  };
  const failed = (e: unknown, slot?: string) => {
    const code = e instanceof PublicBookingError ? e.code : "error";
    if (code === "link_expired") setState({ kind: "expired", rep: e instanceof PublicBookingError ? e.rep : null });
    else if (code === "slot_taken" && slot) {
      // With or without refreshed slots, the picker reloads without the slot that was just taken
      setTaken((t) => [...t, slot]);
      setPickerKey((k) => k + 1);
      setNotice(c.taken);
    } else if (code === "reschedule_limit") setNotice(c.rescheduleLimit);
    else if (code === "in_progress") setNotice(c.inProgress);
    else if (code === "rate_limited") setNotice(c.rateLimited);
    else setNotice(c.unavailable);
  };
  const reschedule = async (iso: string) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      const view = await api.reschedule(token, iso);
      setState({ kind: "ready", view });
      setMoved(true);
      step("idle");
    } catch (e) {
      failed(e, iso);
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      setState({ kind: "ready", view: await api.cancel(token) });
      step("idle");
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  };

  if (state.kind === "loading")
    return (
      <PublicShell lang={lang}>
        <p role="status">{c.loadingBooking}</p>
      </PublicShell>
    );
  if (state.kind === "error")
    return (
      <PublicShell lang={lang}>
        <div role="alert" className="flex flex-col items-start gap-2">
          <p>{state.code === "rate_limited" ? c.rateLimited : c.error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setState({ kind: "loading" });
              setAttempt((n) => n + 1);
            }}
          >
            {c.retry}
          </Button>
        </div>
      </PublicShell>
    );
  if (state.kind === "expired") {
    const rep = state.rep;
    const name = rep ? `${rep.firstName} ${rep.lastName}`.trim() : "";
    return (
      <PublicShell lang={lang} onLang={setLang}>
        <h1 className="text-2xl font-extrabold">{c.manageTitle}</h1>
        <p role="alert" className="mt-2">
          {fill(c.expired, { rep: name || c.yourRep })}
        </p>
        {rep?.email && (
          <a className="mt-2 inline-block text-accent-foreground underline underline-offset-4" href={`mailto:${rep.email}`}>
            {rep.email}
          </a>
        )}
      </PublicShell>
    );
  }

  const v = state.view;
  const when = whenWithZone(v.startsAt, tz, lang);
  const repName = `${v.rep.firstName} ${v.rep.lastName}`.trim();
  const cancelled = v.status === "cancelled";
  return (
    <PublicShell lang={lang} onLang={setLang}>
      <h1 className="text-2xl font-extrabold">{c.manageTitle}</h1>
      {repName && <p className="text-muted-foreground">{fill(c.with, { name: repName })}</p>}
      {cancelled ? (
        <p ref={summaryRef} tabIndex={-1} role="status" className="mt-2 outline-none">
          {c.cancelled}
        </p>
      ) : moved ? (
        <p ref={summaryRef} tabIndex={-1} role="status" title={tz} className="mt-2 outline-none">
          {fill(c.rescheduled, { when })}
        </p>
      ) : (
        <p ref={summaryRef} tabIndex={-1} title={tz} className="mt-2 outline-none">
          {fill(c.doneWhen, { when })}
        </p>
      )}
      {notice && (
        <p role="alert" className="mt-4 rounded-md border border-destructive/40 p-3 text-sm">
          {notice}
        </p>
      )}
      {!cancelled && mode === "idle" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => step("picking")}>{c.reschedule}</Button>
          <Button variant="outline" onClick={() => step("confirmCancel")}>
            {c.cancel}
          </Button>
        </div>
      )}
      {!cancelled && mode === "confirmCancel" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="destructive" disabled={busy} onClick={cancel}>
            {c.confirmCancel}
          </Button>
          <Button variant="ghost" className="text-foreground" onClick={() => step("idle")}>
            {c.keep}
          </Button>
        </div>
      )}
      {!cancelled && mode === "picking" && (
        <div className="mt-4">
          <SlotPicker
            key={pickerKey}
            api={api}
            query={{ token }}
            lang={lang}
            exclude={taken}
            onPick={reschedule}
            onError={(code) => code === "link_expired" && setState({ kind: "expired", rep: v.rep })}
          />
        </div>
      )}
    </PublicShell>
  );
};
